/**
 * Per-asset frame decoder — promoted from the Phase 0 Spike B harness (proven
 * frame-accurate + bounded-memory headlessly). Mediabunny handles demux + the
 * keyframe-seek dance; we keep a bounded LRU of decoded frames as ImageBitmaps.
 *
 * One instance wraps ONE media asset (a proxy clip). The DecoderPool owns the
 * per-asset instances for an EDL.
 */
import {
  ALL_FORMATS,
  BufferSource,
  Input,
  VideoSampleSink,
  type InputVideoTrack,
} from 'mediabunny';

/**
 * Fetch a URL into an ArrayBuffer, reporting download progress (0..1) via the
 * response body stream. Falls back to a plain `arrayBuffer()` (no progress) when
 * the body isn't streamable or Content-Length is missing.
 */
async function fetchWithProgress(
  url: string,
  onProgress?: (fraction: number) => void
): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${res.status} for ${url}`);
  const total = Number(res.headers.get('content-length')) || 0;
  if (!res.body || total <= 0 || !onProgress) return res.arrayBuffer();

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done || !value) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress(Math.min(1, loaded / total));
  }
  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  return out.buffer;
}

export interface DecodedFrame {
  bitmap: ImageBitmap;
  requestedFrame: number;
  sampleFrame: number;
  sampleTimestampSec: number;
  cacheHit: boolean;
}

export class FrameDecoder {
  readonly fps: number;
  private track: InputVideoTrack | null = null;
  private sink: VideoSampleSink | null = null;
  private cache = new Map<number, ImageBitmap>();
  private cap: number;
  private opening: Promise<void> | null = null;
  private durationSec: number | null = null; // lazily computed on first frameCount()

  constructor(fps = 30, cacheCap = 48) {
    this.fps = fps;
    this.cap = cacheCap;
  }

  /**
   * Open the asset from a URL. Idempotent — concurrent calls share one fetch.
   * `onProgress` reports download progress (0..1) when the response is streamable
   * and Content-Length is known; otherwise it isn't called (indeterminate).
   */
  open(url: string, onProgress?: (fraction: number) => void): Promise<void> {
    if (this.opening) return this.opening;
    this.opening = (async () => {
      const buf = await fetchWithProgress(url, onProgress);
      const input = new Input({ source: new BufferSource(buf), formats: ALL_FORMATS });
      const track = await input.getPrimaryVideoTrack();
      if (!track) throw new Error(`no video track in ${url}`);
      if (!(await track.canDecode())) throw new Error(`undecodable track in ${url}`);
      this.track = track;
      this.sink = new VideoSampleSink(track);
    })();
    return this.opening;
  }

  get ready(): boolean {
    return this.sink !== null;
  }
  get width(): number {
    return this.track?.displayWidth ?? 0;
  }
  get height(): number {
    return this.track?.displayHeight ?? 0;
  }

  /**
   * Natural length in fps-timeline frames (for looping short sources like GIFs).
   * 0 until the asset is opened. Duration is computed once, on first request.
   */
  async frameCount(): Promise<number> {
    if (!this.track) return 0;
    if (this.durationSec === null) {
      this.durationSec = await this.track.computeDuration().catch(() => 0);
    }
    return this.durationSec > 0 ? Math.round(this.durationSec * this.fps) : 0;
  }

  /** Decode (or cache-hit) the frame at `frameIndex`, seeking to mid-frame. */
  async frameAt(frameIndex: number): Promise<DecodedFrame> {
    if (!this.sink) throw new Error('decoder not opened');
    const cached = this.cache.get(frameIndex);
    if (cached) {
      this.cache.delete(frameIndex);
      this.cache.set(frameIndex, cached);
      return {
        bitmap: cached,
        requestedFrame: frameIndex,
        sampleFrame: frameIndex,
        sampleTimestampSec: frameIndex / this.fps,
        cacheHit: true,
      };
    }
    const sample = await this.sink.getSample((frameIndex + 0.5) / this.fps);
    if (!sample) throw new Error(`no sample at frame ${frameIndex}`);
    const sampleTimestampSec = sample.timestamp;
    const sampleFrame = Math.round(sampleTimestampSec * this.fps);
    const bitmap = await createImageBitmap(sample.toCanvasImageSource() as CanvasImageSource);
    sample.close();
    this.put(frameIndex, bitmap);
    return { bitmap, requestedFrame: frameIndex, sampleFrame, sampleTimestampSec, cacheHit: false };
  }

  private put(key: number, bitmap: ImageBitmap): void {
    this.cache.set(key, bitmap);
    while (this.cache.size > this.cap) {
      const oldest = this.cache.keys().next().value;
      if (oldest === undefined) break;
      this.cache.get(oldest)?.close();
      this.cache.delete(oldest);
    }
  }

  dispose(): void {
    for (const bm of this.cache.values()) bm.close();
    this.cache.clear();
    this.sink = null;
    this.track = null;
  }
}
